import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CreateUserRequest, DefaultResponse, DeleteUserRequest, EditMyGlobalUserRequest, EditUserRequest, FullUser, GlobalFrontendUser, ListGlobalUsersResponse, ListUsersResponse, MyMandantsResponse, Permissions } from '@reflact/reflactportal';
import { ObjectId } from 'bson';
import { firstValueFrom } from 'rxjs';
import { LoginService } from './login.service';

/** Rollen + individuell freigeschaltete Kacheln eines Nutzers in genau einem Mandanten \u2014 siehe
 * getMandantDetailsForUser(). */
export interface MandantUserDetail {
  _id: string;
  name: string;
  permissions: string[];
  tiles: string[];
  /** Kachel-eigene Zusatzrechte (Keys aus `Tile.customPermissions`), pro zugewiesener Kachel. */
  tilePermissions: Record<string, string[]>;
}
@Injectable({
  providedIn: 'root'
})
export class UserService {

  public async leaveMandant(mandantId: string): Promise<MyMandantsResponse> {
    return await firstValueFrom(this.http.post<MyMandantsResponse>("/api/user/leaveMandant", { mandantId }));
  }

  constructor(public http: HttpClient, public loginService: LoginService) {

  }




  public async getMandantUsers(): Promise<FullUser[]> {
    const res = await firstValueFrom(this.http.get<ListUsersResponse>("/api/user/list"));
    if (res.status == "ok") {
      return res.users;
    }
    return [];
  }

  /** Wie {@link getMandantUsers}, aber für reflact-admins - beliebiger Mandant statt nur dem im
   * Token aktuell gewählten (Mandantenverwaltung zeigt die Mitglieder eines FREMDEN Mandanten). */
  public async getMandantUsersAsReflactAdmin(mandantId: string): Promise<FullUser[]> {
    const res = await firstValueFrom(this.http.get<ListUsersResponse>("/api/user/listForMandantAsReflactAdmin", { params: { mandantId } }));
    return res.status == "ok" ? res.users : [];
  }

  public async getGlobalUsers(): Promise<GlobalFrontendUser[]> {
    const res = await firstValueFrom(this.http.get<ListGlobalUsersResponse>("/api/user/listGlobal"));
    if (res.status == "ok") {
      return res.users;
    }
    return [];
  }

  /** Mandants a given (arbitrary) user belongs to — reflact-admin only, used to populate the
   * "Zu Mandant hinzufügen" panel in global user administration. */
  public async getMandantsForUser(userId: string): Promise<MyMandantsResponse> {
    const res = await firstValueFrom(this.http.get<MyMandantsResponse>(`/api/user/${userId}/mandants`));
    return res;
  }

  /** Rollen + individuell freigeschaltete Kacheln eines Nutzers in jedem seiner Mandanten —
   * reflact-admin only (Super-Administration, siehe global-user-administration). */
  public async getMandantDetailsForUser(userId: string): Promise<DefaultResponse & { mandants: MandantUserDetail[] }> {
    const res = await firstValueFrom(this.http.get<DefaultResponse & { mandants: MandantUserDetail[] }>(`/api/user/${userId}/mandantDetails`));
    return res;
  }

  /** Wie {@link editMandantUser}, aber f\u00fcr reflact-admins \u2014 beliebiger Mandant statt nur dem
   * im Token aktuell gew\u00e4hlten. */
  public async editMandantUserAsReflactAdmin(mandantId: string, userId: string, permissions: Permissions[], excludeCrmIds: string[]): Promise<DefaultResponse> {
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/user/editAsReflactAdmin", { mandantId, userId, permissions, excludeCrmIds }));
    return res;
  }

  /** Wie {@link editMandantUserTiles}, aber f\u00fcr reflact-admins \u2014 beliebiger Mandant. */
  public async setMandantUserTilesAsReflactAdmin(mandantId: string, userId: string, tiles: string[], tilePermissions?: Record<string, string[]>): Promise<DefaultResponse> {
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/user/setTilesAsReflactAdmin", { mandantId, userId, tiles, tilePermissions }));
    return res;
  }

  public async createUser(email: string, name: string, permissions: Permissions[], excludeCrmIds: string[]): Promise<DefaultResponse> {
    const req: CreateUserRequest = {
      email,
      name,
      permissions,
      excludeCrmIds
    }
    const res = firstValueFrom(this.http.post<DefaultResponse>("/api/user/create", req));
    return res;
  }

  public async createUserAsReflactAdmin(email: string, name: string, permissions: Permissions[], mandantId: string, reflactadmin: boolean, excludeCrmIds: string[]): Promise<DefaultResponse> {
    const req = {
      email,
      name,
      permissions,
      mandantId,
      reflactadmin,
      excludeCrmIds
    }
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/user/createAsReflactAdmin", req));
    return res;
  }

  public async deleteMandantUser(userId: ObjectId): Promise<DefaultResponse> {
    const req: DeleteUserRequest = {
      "userId": userId
    }
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/user/delete", req));
    if (userId?.toString() == this.loginService.loggedInUser?._id?.toString()) {
      await this.loginService.refreshToken();
    }
    return res
  }

  public async deleteMandantUserAsReflactAdmin(userId: ObjectId, mandantId: string): Promise<DefaultResponse> {
    const req = {
      userId,
      mandantId
    }
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/user/deleteAsReflactAdmin", req));
    if (userId?.toString() == this.loginService.loggedInUser?._id?.toString()) {
      await this.loginService.refreshToken();
    }
    return res;
  }

  public async editMandantUser(userId: ObjectId, permissions: Permissions[], excludeCrmIds: string[]) {
    const req: EditUserRequest = {
      userId,
      permissions,
      excludeCrmIds
    }
    const res = firstValueFrom(this.http.post<DefaultResponse>("/api/user/edit", req));
    if (userId?.toString() == this.loginService.loggedInUser?._id?.toString()) {
      await this.loginService.refreshToken();
    }
    return res;
  }

  /** Individuell (pro Nutzer) freigeschaltete Kacheln im aktuellen Mandanten \u2014 zweigleisiges
   * Modell, unabh\u00e4ngig von Rollen/Mandanten-weiter Kachel-Zuweisung (siehe tile.service.ts). */
  public async editMandantUserTiles(userId: string, tiles: string[], tilePermissions?: Record<string, string[]>): Promise<DefaultResponse> {
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/user/setTiles", { userId, tiles, tilePermissions }));
    return res;
  }

  public async editMyGlobalUser(email: string, name: string) {
    const req: EditMyGlobalUserRequest = {
      email,
      name
    }
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/user/editMyGlobal", req));
    return res;
  }

  public async editGlobalUser(userId: string, email: string, enableAuthentificator: boolean, name: string, reflactadmin: boolean) {
    const req = {
      userId,
      email,
      enableAuthentificator,
      name,
      reflactadmin
    }
    const res = await firstValueFrom(this.http.post<DefaultResponse>("/api/user/editGlobal", req));
    if (userId == this.loginService.loggedInUser?._id?.toString()) {
      await this.loginService.refreshToken();
    }
    return res;
  }
}
